import { CheckCircle2, Clock, Info, Loader2 } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import type { ShopifyPlanCreditsStatus } from "@/hooks/queries/shopify-plan-credits"

const NOTICE_TEXT: Record<ShopifyPlanCreditsStatus, string> = {
  checking: "Your Shopify subscription is active.",
  credited: "Your Shopify subscription is active, and your plan's credits are in your balance.",
  none: "Your Shopify subscription is active.",
  delayed: "Your Shopify subscription is active. Your plan's credits will appear in your balance, usually within an hour.",
}

/** The page-level outcome of a Shopify plan approval; the progress sits on the balance itself. */
export function ShopifyPlanCreditsNotice({ status }: { status: ShopifyPlanCreditsStatus }) {
  return (
    <Alert role="status" className={status === "credited" ? "[&>svg]:text-green-600" : undefined}>
      <NoticeIcon status={status} />
      {/* Keyed so a translated page shows the new state instead of the old translated text. */}
      <AlertDescription key={status}>
        <span>{NOTICE_TEXT[status]}</span>
      </AlertDescription>
    </Alert>
  )
}

/** Shown under the balance while the plan's credits are on their way. */
export function ShopifyPlanCreditsProgress({ status }: { status: ShopifyPlanCreditsStatus }) {
  if (status !== "checking") return null
  return (
    <p role="status" className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <span>Adding your plan&apos;s credits…</span>
    </p>
  )
}

function NoticeIcon({ status }: { status: ShopifyPlanCreditsStatus }) {
  switch (status) {
    case "checking":
    case "none":
      return <Info className="h-4 w-4" />
    case "credited":
      return <CheckCircle2 className="h-4 w-4" />
    case "delayed":
      return <Clock className="h-4 w-4" />
  }
}
